/**
 * Where the visits map opens, as a pure rule, so it can be checked without a
 * browser (13 October). FILTER FIRST, THEN FIT: the points given here are
 * already the filtered set, the reader's own places.
 */

/**
 * Below this many places the map fits every one of them; at and above it, the
 * 5th to 95th percentile. A percentile fit exists to stop a few far-off places
 * (upstate, New Jersey) stretching the box over empty country; on a small set
 * every pin is a person somebody must visit, and trimming one off screen costs
 * more than a wider view. Fifty keeps every small caseload whole (the seed's
 * Social Work caseloads, drawn from the real ones, are 1, 2, 3, 12, 192 and 236
 * clients) and trims only the agency and the two large ones. With nearest rank,
 * a 5% tail of fifty is two places at each end of each axis; latitude and
 * longitude are trimmed separately, so up to eight places can fall outside, and
 * the map says how many did.
 */
export const FIT_ALL_BELOW = 50;

/**
 * The value at percentile `p` of sorted values, by nearest rank: always one of
 * the values, never a point between two places.
 */
function nearestRank(sorted: number[], p: number): number {
  const rank = Math.min(sorted.length, Math.max(1, Math.ceil(p * sorted.length)));
  return sorted[rank - 1]!;
}

/**
 * The box the map opens on, for these points: every point below
 * `FIT_ALL_BELOW`, else the 5th to 95th percentile of latitude and of
 * longitude, each on its own. Null for none; a single point is a centre.
 */
export function fitFor(points: [number, number][]): { centre: [number, number] } | { box: [[number, number], [number, number]] } | null {
  if (points.length === 0) return null;
  const lats = points.map((p) => p[0]).sort((a, b) => a - b);
  const lons = points.map((p) => p[1]).sort((a, b) => a - b);
  const [lo, hi] = points.length < FIT_ALL_BELOW ? [0, 1] : [0.05, 0.95];
  const south = lo === 0 ? lats[0]! : nearestRank(lats, lo);
  const north = hi === 1 ? lats[lats.length - 1]! : nearestRank(lats, hi);
  const west = lo === 0 ? lons[0]! : nearestRank(lons, lo);
  const east = hi === 1 ? lons[lons.length - 1]! : nearestRank(lons, hi);
  if (south === north && west === east) return { centre: [south, west] };
  return { box: [[south, west], [north, east]] };
}
